import React, { useMemo, useState } from 'react';
import Select, { StylesConfig, components as selectComponents, MenuListProps } from 'react-select';
import classNames from 'classnames';
import { ReactComponent as DirectionSvg } from "../assets/svg/direction.svg";

export interface SearchableOption {
    value: string;
    label: string;
}

interface SearchableDropdownProps {
    className?: string;
    label?: string;
    required?: boolean;
    options?: SearchableOption[];
    value?: string;
    placeholder?: string;
    isLarge?: boolean;
    isBold?: boolean;
    hasBorder?: boolean;
    disabled?: boolean;
    maxDisplayed?: number;
    onChange?: (option: SearchableOption | null) => void;
    footer?: React.ReactNode;
}

const CustomDropdownIndicator = () => (
    <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px' }}>
        <DirectionSvg />
    </div>
);

const FooterItem: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [hover, setHover] = useState(false);
    return (
        <div
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                borderTop: '1px solid #e5e7eb',
                padding: '10px 12px',
                cursor: 'pointer',
                backgroundColor: hover ? '#1C0054' : 'transparent',
                color: hover ? '#ffffff' : '#1C0054',
                fontSize: 14,
                fontWeight: 500,
                transition: 'background-color 120ms ease, color 120ms ease',
            }}
        >
            {children}
        </div>
    );
};

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
    className,
    label,
    required,
    options = [],
    value,
    placeholder,
    isLarge = false,
    isBold = false,
    hasBorder = true,
    disabled = false,
    maxDisplayed = 10,
    onChange,
    footer,
}) => {
    const [inputValue, setInputValue] = useState('');

    const selectedOption = useMemo(
        () => (value ? options.find(o => o.value === value) : null) ?? null,
        [options, value]
    );

    // When not searching, show first maxDisplayed options + always include selected.
    // When searching, show all options (react-select filters by inputValue).
    const displayedOptions = useMemo(() => {
        if (inputValue) return options;
        const sliced = options.slice(0, maxDisplayed);
        if (selectedOption && !sliced.some(o => o.value === selectedOption.value)) {
            return [selectedOption, ...sliced];
        }
        return sliced;
    }, [options, inputValue, maxDisplayed, selectedOption]);

    const fontSize = isLarge ? 20 : 15;
    const fontWeight = isBold ? 500 : 400;
    const fontFamily = isBold ? 'Urbanist-Medium' : undefined;

    const customStyles: StylesConfig<SearchableOption, false> = {
        control: (base, state) => ({
            ...base,
            borderRadius: 8,
            padding: '2px 4px',
            border: hasBorder ? '1px solid #1C0054' : 'none',
            boxShadow: state.isFocused ? '0 0 0 1px #1C0054' : 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            minHeight: 45,
            fontSize,
            fontWeight,
            fontFamily,
            backgroundColor: disabled ? '#f3f4f6' : 'transparent',
            opacity: disabled ? 0.7 : 1,
            '&:hover': {
                border: hasBorder ? '1px solid #1C0054' : 'none',
            },
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 4px',
            justifyContent: 'flex-start',
        }),
        singleValue: (base) => ({
            ...base,
            fontSize,
            fontWeight,
            fontFamily,
            color: '#1C0054',
        }),
        input: (base) => ({
            ...base,
            fontSize,
            fontWeight,
            fontFamily,
            margin: 0,
            padding: 0,
        }),
        placeholder: (base) => ({
            ...base,
            fontSize,
            fontWeight,
            fontFamily,
            color: '#6B7280',
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '0 4px',
            svg: { width: 12, height: 12 },
        }),
        menu: (base) => ({
            ...base,
            borderRadius: 8,
        }),
        menuList: (base) => ({
            ...base,
            maxHeight: 250,
            borderRadius: 8,
        }),
        option: (base, state) => ({
            ...base,
            fontSize: 14,
            fontFamily,
            cursor: 'pointer',
            padding: '10px 12px',
            backgroundColor: state.isSelected ? '#1C0054' : state.isFocused ? '#f3f0ff' : 'transparent',
            color: state.isSelected ? '#fff' : '#1C0054',
            '&:active': {
                backgroundColor: '#e8e0ff',
            },
        }),

    };

    return (
        <div className={classNames(className)}>
            {label && (
                <div className="d-flex font-xs font-bold mb-2">
                    {label}{required && <span className="text-danger ms-1">*</span>}
                </div>
            )}
            <Select<SearchableOption, false>
                options={displayedOptions}
                value={selectedOption}
                onChange={onChange}
                placeholder={placeholder}
                isSearchable
                isDisabled={disabled || options.length < 1}
                inputValue={inputValue}
                onInputChange={(val: string, action: { action: string }) => {
                    if (action.action === 'input-change') setInputValue(val);
                    if (action.action === 'menu-close') setInputValue('');
                }}
                components={{
                    DropdownIndicator: CustomDropdownIndicator,
                    ...(footer
                        ? {
                            MenuList: (menuListProps: MenuListProps<SearchableOption, false>) => {
                                const BaseMenuList = selectComponents.MenuList as unknown as React.ComponentType<MenuListProps<SearchableOption, false>>;
                                return (
                                <BaseMenuList {...menuListProps}>
                                    {menuListProps.children}
                                    <FooterItem>{footer}</FooterItem>
                                </BaseMenuList>
                                );
                            },
                        }
                        : {}),
                }}
                styles={customStyles}
                menuPlacement="auto"
            />
        </div>
    );
};

export default SearchableDropdown;
